import { useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  query,
  orderBy,
  limit,
  onSnapshot,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import {
  Radio,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MapPin,
  Hash,
  Calendar,
  Users,
  Save,
  Power,
  Clock,
  Plus,
  X,
} from 'lucide-react';

// Variables available in the SINGLE-order template. No multi-order
// specific ones (subOrdersCount / deliveryCities / deliveryCount /
// multiOrderTag) — those live in the multi template's chip list below.
const VARIABLES_SINGLE = [
  { key: 'pickupCity', desc: 'Pickup city' },
  { key: 'pickupArea', desc: 'Pickup area' },
  { key: 'pickupAddress', desc: 'Pickup full address' },
  { key: 'deliveryCity', desc: 'Delivery city' },
  { key: 'deliveryArea', desc: 'Delivery area' },
  { key: 'deliveryAddress', desc: 'Delivery full address' },
  { key: 'itemPrice', desc: 'Item price' },
  { key: 'deliveryPrice', desc: 'Delivery price' },
  { key: 'totalPrice', desc: 'Total price' },
  { key: 'orderNumber', desc: 'Order #' },
  { key: 'customerName', desc: 'Customer name' },
  { key: 'distance', desc: 'Distance (km)' },
  { key: 'description', desc: 'Order note' },
];

// Variables available in the MULTI-order template. Adds count +
// destination-summary variables that only make sense across siblings.
const VARIABLES_MULTI = [
  { key: 'subOrdersCount', desc: 'Number of sub-orders (2+)' },
  { key: 'deliveryCities', desc: 'Comma-joined list of destination cities' },
  { key: 'deliveryCount', desc: 'Number of unique destinations' },
  { key: 'pickupCity', desc: 'Pickup city' },
  { key: 'pickupArea', desc: 'Pickup area' },
  { key: 'pickupAddress', desc: 'Pickup full address' },
  { key: 'itemPrice', desc: 'Item price (sum of all sub-orders)' },
  { key: 'deliveryPrice', desc: 'Delivery price (sum of all sub-orders)' },
  { key: 'totalPrice', desc: 'Total price (sum of all sub-orders)' },
  { key: 'orderNumber', desc: 'Order #' },
  { key: 'multiOrderTag', desc: 'Multi-order badge (legacy — prefer {subOrdersCount})' },
  { key: 'customerName', desc: 'Customer (first sub-order)' },
];

const DEFAULT_CFG = {
  enabled: true,
  // Single-order pair: rendered when the customer created exactly one
  // sub-order.
  messageTitle: 'طلب جديد',
  messageTemplate: 'طلب في ({pickupArea}) اللي قريب يفتح وينطلق',
  messageTitleEn: 'New order',
  messageTemplateEn: 'New order in ({pickupArea}) — opening soon',
  // Multi-order pair: rendered when the customer created 2+ sub-orders
  // sharing the same orderNumber. Different phrasing + access to
  // {subOrdersCount}, {deliveryCities}, {deliveryCount}.
  multiMessageTitle: 'طلب متعدد',
  multiMessageTemplate: '🛒 {subOrdersCount} طلبات في ({pickupArea}) — التسليم إلى: {deliveryCities}',
  multiMessageTitleEn: 'Multi-order',
  multiMessageTemplateEn: '🛒 {subOrdersCount} orders in ({pickupArea}) — delivering to: {deliveryCities}',
  retryMinutes: [5, 15],
};

/**
 * Driver broadcasts (auto-fired by Cloud Function `broadcastNewOrderToDrivers`
 * when a new order is created). This page is read-only — it just shows that
 * each order was actually pushed to all active drivers, and how many
 * recipients succeeded vs. failed.
 */
export default function BroadcastHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const { toast } = useToast();
  const { isMasterAdmin, user: currentAdmin } = useAuth();
  const canEditConfig = !!currentAdmin && (
    currentAdmin.type === 'admin' || currentAdmin.type === 'admin_master'
  );

  // Broadcast config (lives at app_settings/broadcast_config)
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [cfgSaving, setCfgSaving] = useState(false);
  const [newRetry, setNewRetry] = useState('');
  const templateRef = useRef(null);
  const multiTemplateRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'app_settings', 'broadcast_config'));
        if (snap.exists()) {
          const data = snap.data();
          setCfg({
            enabled: data.enabled !== false,
            messageTitle: data.messageTitle || DEFAULT_CFG.messageTitle,
            messageTemplate: data.messageTemplate || DEFAULT_CFG.messageTemplate,
            messageTitleEn: data.messageTitleEn || DEFAULT_CFG.messageTitleEn,
            messageTemplateEn: data.messageTemplateEn || DEFAULT_CFG.messageTemplateEn,
            multiMessageTitle: data.multiMessageTitle || DEFAULT_CFG.multiMessageTitle,
            multiMessageTemplate: data.multiMessageTemplate || DEFAULT_CFG.multiMessageTemplate,
            multiMessageTitleEn: data.multiMessageTitleEn || DEFAULT_CFG.multiMessageTitleEn,
            multiMessageTemplateEn: data.multiMessageTemplateEn || DEFAULT_CFG.multiMessageTemplateEn,
            retryMinutes: Array.isArray(data.retryMinutes)
              ? data.retryMinutes.map(Number).filter((n) => Number.isFinite(n) && n > 0)
              : DEFAULT_CFG.retryMinutes,
          });
        }
      } catch (e) {
        console.error('Failed to load broadcast config:', e);
      } finally {
        setCfgLoading(false);
      }
    })();
  }, []);

  // Insert a {variable} token into the given template field. The `refEl`
  // is the AR textarea for that template so we insert at the caret when
  // one is focused; otherwise we just append. Extracted from the
  // single-template-only version so the multi-order template chips can
  // share the same behavior against their own AR textarea.
  const insertVariableInto = (fieldKey, refEl, key) => {
    const el = refEl?.current;
    const token = `{${key}}`;
    if (!el) {
      setCfg((c) => ({ ...c, [fieldKey]: (c[fieldKey] || '') + token }));
      return;
    }
    const current = cfg[fieldKey] || '';
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    setCfg((c) => ({ ...c, [fieldKey]: next }));
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        const caret = start + token.length;
        el.setSelectionRange(caret, caret);
      }
    });
  };
  const insertVariable = (key) => insertVariableInto('messageTemplate', templateRef, key);

  const addRetry = () => {
    const n = Number(newRetry);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error('Enter a positive number of minutes');
      return;
    }
    if (cfg.retryMinutes.includes(n)) {
      setNewRetry('');
      return;
    }
    setCfg((c) => ({
      ...c,
      retryMinutes: [...c.retryMinutes, n].sort((a, b) => a - b),
    }));
    setNewRetry('');
  };

  const removeRetry = (n) => {
    setCfg((c) => ({ ...c, retryMinutes: c.retryMinutes.filter((x) => x !== n) }));
  };

  const saveConfig = async () => {
    if (!canEditConfig) return;
    if (!cfg.messageTemplate.trim()) {
      toast.error('Single-order message template cannot be empty');
      return;
    }
    if (!cfg.multiMessageTemplate.trim()) {
      toast.error('Multi-order message template cannot be empty');
      return;
    }
    try {
      setCfgSaving(true);
      await setDoc(doc(db, 'app_settings', 'broadcast_config'), {
        enabled: !!cfg.enabled,
        messageTitle: cfg.messageTitle.trim(),
        messageTemplate: cfg.messageTemplate,
        messageTitleEn: (cfg.messageTitleEn || '').trim(),
        messageTemplateEn: cfg.messageTemplateEn || '',
        multiMessageTitle: (cfg.multiMessageTitle || '').trim(),
        multiMessageTemplate: cfg.multiMessageTemplate || '',
        multiMessageTitleEn: (cfg.multiMessageTitleEn || '').trim(),
        multiMessageTemplateEn: cfg.multiMessageTemplateEn || '',
        retryMinutes: cfg.retryMinutes,
        updatedAt: serverTimestamp(),
        updatedBy: currentAdmin?.email || currentAdmin?.name || null,
      }, { merge: true });
      logAdminActivity({
        actor: currentAdmin, action: 'update_broadcast_config',
        targetType: 'broadcast_config', targetId: 'app_settings/broadcast_config',
        targetLabel: 'Broadcast config',
      });
      toast.success('Broadcast settings saved');
    } catch (e) {
      console.error(e);
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setCfgSaving(false);
    }
  };

  const toggleEnabled = async (next) => {
    if (!canEditConfig) return;
    setCfg((c) => ({ ...c, enabled: next }));
    try {
      await setDoc(doc(db, 'app_settings', 'broadcast_config'), {
        enabled: next,
        updatedAt: serverTimestamp(),
        updatedBy: currentAdmin?.email || currentAdmin?.name || null,
      }, { merge: true });
      logAdminActivity({
        actor: currentAdmin, action: 'toggle_broadcasts',
        targetType: 'broadcast_config', targetId: 'app_settings/broadcast_config',
        targetLabel: next ? 'ON' : 'OFF',
      });
      toast.success(next ? 'Broadcasts turned ON' : 'Broadcasts turned OFF');
    } catch (e) {
      console.error(e);
      toast.error(`Toggle failed: ${e.message}`);
      setCfg((c) => ({ ...c, enabled: !next })); // revert
    }
  };

  useEffect(() => {
    const q = query(
      collection(db, 'driver_broadcasts'),
      orderBy('createdAt', 'desc'),
      limit(200),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRows(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate?.() ?? null,
          })),
        );
        setLoading(false);
      },
      (e) => {
        console.error('broadcasts listener:', e);
        setErr(e.message || 'Failed to load broadcasts');
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  const fmt = (d) => (d ? d.toLocaleString('en-GB') : '—');

  const statusBadge = (b) => {
    if (b.status === 'sent')
      return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Sent</Badge>;
    if (b.status === 'partial')
      return <Badge variant="warning"><AlertTriangle className="w-3 h-3 mr-1" />Partial</Badge>;
    if (b.status === 'failed')
      return <Badge variant="error"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
    return <Badge variant="default">{b.status || 'unknown'}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Radio className="w-7 h-7 text-blue-600" />
            Driver broadcasts
          </h1>
          <p className="text-gray-600 mt-1">
            Auto-notifications sent to every active driver each time a new
            order is created.
          </p>
        </div>
      </div>

      {/* Broadcast settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Radio className="w-5 h-5 text-blue-600" />
                Broadcast settings
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Customize the auto-notification sent to drivers when a new order is created.
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleEnabled(!cfg.enabled)}
              disabled={!canEditConfig || cfgLoading}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                cfg.enabled
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-red-100 text-red-800 hover:bg-red-200'
              }`}
              title={cfg.enabled ? 'Click to disable' : 'Click to enable'}
            >
              <Power className="w-4 h-4" />
              {cfg.enabled ? 'Broadcast ON' : 'Broadcast OFF'}
            </button>
          </div>
        </CardHeader>
        <CardBody>
          {cfgLoading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : (
            <div className="space-y-8">
              {/* ============================================================
                  SINGLE-ORDER TEMPLATE
                  Rendered when the customer created exactly one sub-order.
                  ============================================================ */}
              <section className="rounded-xl border border-slate-200 p-4 bg-slate-50/40">
                <header className="mb-3">
                  <h3 className="text-sm font-semibold text-slate-800">
                    Single-order template
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Sent when the order has one delivery. Both AR + EN copies
                    go out — driver app picks based on the driver's userLang.
                  </p>
                </header>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title — العربية</label>
                      <input
                        type="text"
                        value={cfg.messageTitle}
                        onChange={(e) => setCfg((c) => ({ ...c, messageTitle: e.target.value }))}
                        disabled={!canEditConfig}
                        dir="rtl"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 text-sm"
                        placeholder="طلب جديد"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title — English</label>
                      <input
                        type="text"
                        value={cfg.messageTitleEn || ''}
                        onChange={(e) => setCfg((c) => ({ ...c, messageTitleEn: e.target.value }))}
                        disabled={!canEditConfig}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 text-sm"
                        placeholder="New order"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Body</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-[11px] font-medium text-slate-500 mb-1">العربية</p>
                        <textarea
                          ref={templateRef}
                          value={cfg.messageTemplate}
                          onChange={(e) => setCfg((c) => ({ ...c, messageTemplate: e.target.value }))}
                          disabled={!canEditConfig}
                          rows={3}
                          dir="rtl"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 text-sm font-mono"
                          placeholder="طلب في ({pickupArea}) ..."
                        />
                      </div>
                      <div>
                        <p className="text-[11px] font-medium text-slate-500 mb-1">English</p>
                        <textarea
                          value={cfg.messageTemplateEn || ''}
                          onChange={(e) => setCfg((c) => ({ ...c, messageTemplateEn: e.target.value }))}
                          disabled={!canEditConfig}
                          rows={3}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 text-sm font-mono"
                          placeholder="New order in ({pickupArea}) — opening soon"
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {VARIABLES_SINGLE.map((v) => (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => insertVariableInto('messageTemplate', templateRef, v.key)}
                          disabled={!canEditConfig}
                          title={v.desc}
                          className="px-2 py-1 rounded-md text-xs font-mono bg-white ring-1 ring-slate-200 hover:bg-blue-50 hover:text-blue-700 transition-colors disabled:opacity-50"
                        >
                          {`{${v.key}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* ============================================================
                  MULTI-ORDER TEMPLATE
                  Rendered when the customer created 2+ sub-orders sharing
                  the same orderNumber. Prices ({itemPrice}, {deliveryPrice},
                  {totalPrice}) are already SUMMED across siblings.
                  ============================================================ */}
              <section className="rounded-xl border border-blue-200 p-4 bg-blue-50/30">
                <header className="mb-3">
                  <h3 className="text-sm font-semibold text-slate-800">
                    Multi-order template
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Sent when the customer created <strong>2+ sub-orders</strong> at once.
                    Prices are summed across siblings, and <code className="px-1 rounded bg-blue-100 text-blue-800">{'{deliveryCities}'}</code> /{' '}
                    <code className="px-1 rounded bg-blue-100 text-blue-800">{'{deliveryCount}'}</code> list all destinations.
                  </p>
                </header>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title — العربية</label>
                      <input
                        type="text"
                        value={cfg.multiMessageTitle || ''}
                        onChange={(e) => setCfg((c) => ({ ...c, multiMessageTitle: e.target.value }))}
                        disabled={!canEditConfig}
                        dir="rtl"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 text-sm"
                        placeholder="طلب متعدد"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title — English</label>
                      <input
                        type="text"
                        value={cfg.multiMessageTitleEn || ''}
                        onChange={(e) => setCfg((c) => ({ ...c, multiMessageTitleEn: e.target.value }))}
                        disabled={!canEditConfig}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 text-sm"
                        placeholder="Multi-order"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Body</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-[11px] font-medium text-slate-500 mb-1">العربية</p>
                        <textarea
                          ref={multiTemplateRef}
                          value={cfg.multiMessageTemplate || ''}
                          onChange={(e) => setCfg((c) => ({ ...c, multiMessageTemplate: e.target.value }))}
                          disabled={!canEditConfig}
                          rows={3}
                          dir="rtl"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 text-sm font-mono"
                          placeholder="🛒 {subOrdersCount} طلبات في ({pickupArea}) — التسليم إلى: {deliveryCities}"
                        />
                      </div>
                      <div>
                        <p className="text-[11px] font-medium text-slate-500 mb-1">English</p>
                        <textarea
                          value={cfg.multiMessageTemplateEn || ''}
                          onChange={(e) => setCfg((c) => ({ ...c, multiMessageTemplateEn: e.target.value }))}
                          disabled={!canEditConfig}
                          rows={3}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 text-sm font-mono"
                          placeholder="🛒 {subOrdersCount} orders in ({pickupArea}) — delivering to: {deliveryCities}"
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {VARIABLES_MULTI.map((v) => (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => insertVariableInto('multiMessageTemplate', multiTemplateRef, v.key)}
                          disabled={!canEditConfig}
                          title={v.desc}
                          className="px-2 py-1 rounded-md text-xs font-mono bg-white ring-1 ring-blue-200 hover:bg-blue-100 hover:text-blue-800 transition-colors disabled:opacity-50"
                        >
                          {`{${v.key}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* Retry schedule */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Retry schedule (minutes after order creation)
                </label>
                <p className="text-xs text-slate-500 mb-2">
                  If the order is still waiting and unassigned, broadcast again at each
                  of these offsets. Leave empty to disable retries.
                </p>
                <div className="flex flex-wrap gap-2 items-center">
                  {cfg.retryMinutes.map((m) => (
                    <span
                      key={m}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold"
                    >
                      +{m} min
                      {canEditConfig && (
                        <button
                          type="button"
                          onClick={() => removeRetry(m)}
                          className="hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  {canEditConfig && (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="1"
                        value={newRetry}
                        onChange={(e) => setNewRetry(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addRetry();
                          }
                        }}
                        placeholder="minutes"
                        className="w-24 px-2 py-1 border border-slate-300 rounded-md text-sm"
                      />
                      <button
                        type="button"
                        onClick={addRetry}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                      >
                        <Plus className="w-3 h-3" />
                        Add
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {canEditConfig && (
                <div className="pt-2 flex justify-end">
                  <Button
                    variant="primary"
                    onClick={saveConfig}
                    disabled={cfgSaving}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {cfgSaving ? 'Saving...' : 'Save settings'}
                  </Button>
                </div>
              )}
              {!canEditConfig && (
                <p className="text-xs text-slate-500">
                  Read-only — only admins can edit these settings.
                </p>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent broadcasts ({rows.length})
            </h2>
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => { /* listener is realtime — button just spins */ }}
              title="Real-time. Updates automatically."
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {err ? (
            <div className="p-6 text-red-600 text-sm">Error: {err}</div>
          ) : loading ? (
            <div className="p-12 text-center text-gray-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              No broadcasts yet. They appear here automatically whenever a new
              order is created.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">
                      <Hash className="w-3 h-3 inline mr-1" /> Order
                    </th>
                    <th className="text-left px-4 py-3">
                      <MapPin className="w-3 h-3 inline mr-1" /> Start address
                    </th>
                    <th className="text-left px-4 py-3" title="All drivers / active drivers / active with FCM token">
                      <Users className="w-3 h-3 inline mr-1" /> Drivers
                    </th>
                    <th className="text-left px-4 py-3">Sent</th>
                    <th className="text-left px-4 py-3">Failed</th>
                    <th className="text-left px-4 py-3">
                      <Calendar className="w-3 h-3 inline mr-1" /> When
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((b) => (
                    <tr key={b.id} className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-3">{statusBadge(b)}</td>
                      <td className="px-4 py-3 font-medium">
                        {b.orderNumber ? `#${b.orderNumber}` : '—'}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {b.orderId}
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <div className="truncate" title={b.startAddress}>
                          {b.startAddress || '—'}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5 truncate">
                          {b.body}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs leading-tight">
                        <div>
                          <span className="font-semibold">{b.totalRecipients ?? '—'}</span>
                          <span className="text-gray-400"> recipients</span>
                        </div>
                        {b.totalDrivers != null && (
                          <div className="text-gray-500">
                            of {b.activeDrivers ?? '?'} active / {b.totalDrivers} total
                          </div>
                        )}
                        {(b.activeWithoutToken ?? 0) > 0 && (
                          <div className="text-amber-600" title="Active drivers with no fcmToken (uninstalled / never granted notifications)">
                            {b.activeWithoutToken} active w/o token
                          </div>
                        )}
                        {b.driversByStatus && (
                          <div className="text-gray-400 mt-1">
                            {Object.entries(b.driversByStatus)
                              .filter(([s]) => s !== 'active')
                              .map(([s, n]) => `${s}: ${n}`)
                              .join(' · ') || null}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-green-600 font-medium">
                        {b.sent ?? '—'}
                      </td>
                      <td className={`px-4 py-3 font-medium ${(b.failed ?? 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {b.failed ?? '—'}
                        {(b.invalidTokensRemoved ?? 0) > 0 && (
                          <div className="text-xs text-gray-500 font-normal">
                            {b.invalidTokensRemoved} dead tokens removed
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {fmt(b.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
